import React, { useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import {
  FloatingPortal,
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  arrow,
} from '@floating-ui/react';
import { isNil } from 'lodash';
import Icon from '../../../components/icon/Icon';
import IconButton from '../../../components/button/IconButton';
import { useClickOutside } from '../../../hooks/useClickOutside';
import useEventListener from '../../../hooks/useEventListener';
import QuotePdfPreview from '../../../components/FileManager/QuotePdfPreview';
import { QuotePdfProps } from '../../../components/FileManager/PdfGenerator';
import { DialogTrigger } from '../../../mp/components/Dialog';
import text from '../../../text';
import EditPriceMenuItem from './EditPriceMenuItem';

type Props = {
  scrollContainerRef: React.RefObject<HTMLElement>;
  quotePdfProps: (() => Promise<QuotePdfProps>) | null;
  visibility: {
    isDeleteVisible: boolean;
    isSupersedeVisible: boolean;
    isHideVisible: boolean;
    isEditPriceVisible: boolean;
    isSuperseded: boolean;
  };
  currentUserIsStaff: boolean;
  projectId: number | null | undefined;
  isHidden: boolean;
  onSupersede: () => void;
  onDeleteActivity: () => void;
  onToggleHidden: () => void;
};

const DropdownMenu = ({
  scrollContainerRef,
  quotePdfProps,
  visibility,
  currentUserIsStaff,
  projectId,
  isHidden,
  onSupersede,
  onDeleteActivity,
  onToggleHidden,
}: Props) => {
  const {
    isDeleteVisible,
    isSupersedeVisible,
    isHideVisible,
    isEditPriceVisible,
    isSuperseded,
  } = visibility;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const arrowRef = useRef(null);
  const { refs, floatingStyles, middlewareData, placement } = useFloating({
    open: isDropdownOpen,
    onOpenChange: setIsDropdownOpen,
    middleware: [
      offset(6),
      flip(),
      shift(),
      arrow({ element: arrowRef, padding: 5 }),
    ],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-end',
  });

  const isFlipped = placement?.includes('top');

  const arrowStyles: React.CSSProperties = middlewareData.arrow
    ? {
        left:
          middlewareData.arrow.x != null ? `${middlewareData.arrow.x}px` : '',
        top:
          middlewareData.arrow.y != null ? `${middlewareData.arrow.y}px` : '',
        position: 'absolute',
        width: '12px',
        height: '12px',
        background: 'white',
        border: '1px solid rgba(0,0,0,0.15)',
        ...(isFlipped
          ? {
              bottom: '-6px',
              transform: 'rotate(45deg)',
              borderWidth: '0 1px 1px 0',
            }
          : {
              top: '-6px',
              transform: 'rotate(45deg)',
              borderWidth: '1px 0 0 1px',
            }),
      }
    : {};

  useClickOutside(
    refs.reference,
    () => setIsDropdownOpen(false),
    ['.ActivityDropdownMenu'],
    false
  );

  useEventListener(
    'scroll',
    () => {
      if (isDropdownOpen) setIsDropdownOpen(false);
    },
    window,
    { capture: true }
  );

  const shouldShowMenu =
    isDeleteVisible ||
    isSupersedeVisible ||
    isHideVisible ||
    isEditPriceVisible ||
    !!quotePdfProps;

  return (
    <QuotePdfPreview
      quote={quotePdfProps}
      rootRef={scrollContainerRef}
      noTrigger
    >
      {shouldShowMenu ? (
        <div className='ActivityDropdownMenuContainer position-relative'>
          <IconButton
            icon='more'
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className='ActivityDropdownMenuTrigger ml-2'
            ref={refs.setReference}
          />
          {isDropdownOpen ? (
            <FloatingPortal>
              <div
                ref={refs.setFloating}
                style={{ ...floatingStyles }}
                className='ActivityDropdownMenu dropdown-menu show'
              >
                <div
                  ref={arrowRef}
                  style={{
                    ...arrowStyles,
                    boxShadow: isFlipped
                      ? '2px 2px 2.546px 0px rgba(0, 0, 0, 0.04)'
                      : '-2px -2px 2.546px 0px rgba(0, 0, 0, 0.04)',
                  }}
                  className='dropdown-arrow'
                />

                {quotePdfProps ? (
                  <DialogTrigger asChild>
                    <Dropdown.Item
                      className='py-2'
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <div className='d-flex flex-row align-items-center'>
                        <Icon
                          icon='pdf-file-download'
                          className='mr-2 size-20 default-stroke'
                          style={{ marginLeft: '-2px' }}
                        />
                        <span className='font-14'>Preview PDF</span>
                      </div>
                    </Dropdown.Item>
                  </DialogTrigger>
                ) : null}

                {isSupersedeVisible ? (
                  <Dropdown.Item
                    onClick={() => {
                      onSupersede();
                      setIsDropdownOpen(false);
                    }}
                    className='py-2'
                  >
                    <div className='d-flex flex-row align-items-center'>
                      <Icon
                        icon={isSuperseded ? 'u_folder-add' : 'u_folder-minus'}
                        className='original default-stroke mr-2'
                      />
                      <p className='font-14 m-0'>
                        {isSuperseded
                          ? 'Reverse supersede to include '
                          : 'Supersede to exclude '}
                        from &quot;Download all&quot;
                      </p>
                    </div>
                  </Dropdown.Item>
                ) : null}

                {isEditPriceVisible ? (
                  <EditPriceMenuItem
                    onSelect={() => setIsDropdownOpen(false)}
                  />
                ) : null}

                {isHideVisible ? (
                  <Dropdown.Item
                    onClick={() => {
                      onToggleHidden();
                      setIsDropdownOpen(false);
                    }}
                    className='py-2'
                  >
                    <div className='d-flex flex-row align-items-center'>
                      <Icon
                        icon={isHidden ? 'eye' : 'eye-slash'}
                        className='mr-2 size-20'
                      />
                      <p className='font-14 m-0 mt-1'>
                        {isHidden
                          ? text('showActivity')
                          : text('hideActivity')}
                      </p>
                    </div>
                  </Dropdown.Item>
                ) : null}

                {currentUserIsStaff && !isNil(projectId) ? (
                  <Dropdown.Item
                    onClick={() => {
                      onDeleteActivity();
                      setIsDropdownOpen(false);
                    }}
                    className='py-2'
                  >
                    <div className='d-flex flex-row align-items-center'>
                      <Icon
                        icon='trash'
                        className='mr-2 size-20 default-stroke danger'
                        style={{ marginLeft: '-2px' }}
                      />
                      <p className='font-14 m-0 mt-1 font-danger'>
                        {text('deleteActivity')}
                      </p>
                    </div>
                  </Dropdown.Item>
                ) : null}
              </div>
            </FloatingPortal>
          ) : null}
        </div>
      ) : null}
    </QuotePdfPreview>
  );
};

export default DropdownMenu;
